import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, CircularProgress, Box } from '@mui/material';
import { theme } from './theme';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SnackbarProvider } from './contexts/SnackbarContext';
import { DashboardLayout } from './layouts/DashboardLayout';

// Lazy-loaded pages
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const ContactsPage = React.lazy(() => import('./pages/ContactsPage'));
const ConventionsPage = React.lazy(() => import('./pages/ConventionsPage'));
const ConventionFormPage = React.lazy(() => import('./pages/ConventionFormPage'));
const ConventionDetailPage = React.lazy(() => import('./pages/ConventionDetailPage'));
const AllPhasesPage = React.lazy(() => import('./pages/AllPhasesPage'));
const AdminUsersPage = React.lazy(() => import('./pages/AdminUsersPage'));
const AdminConfigPage = React.lazy(() => import('./pages/AdminConfigPage'));
const EmailsAdminPage = React.lazy(() => import('./pages/EmailsAdminPage'));
const RemindersAdminPage = React.lazy(() => import('./pages/RemindersAdminPage'));
const ImportPage = React.lazy(() => import('./pages/ImportPage'));
const HelpPage = React.lazy(() => import('./pages/HelpPage'));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));
const CalendarPage = React.lazy(() => import('./pages/CalendarPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function LoadingFallback() {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
      <CircularProgress />
    </Box>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingFallback />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route
          path="/login"
          element={
            isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />
          }
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="contactos" element={<ContactsPage />} />
          <Route path="convenios" element={<ConventionsPage />} />
          <Route path="convenios/nuevo" element={<ConventionFormPage />} />
          <Route path="convenios/:id" element={<ConventionDetailPage />} />
          <Route path="convenios/:id/editar" element={<ConventionFormPage />} />
          <Route path="fases" element={<AllPhasesPage />} />
          <Route path="ayuda" element={<HelpPage />} />
          <Route path="admin/users" element={<AdminUsersPage />} />
          <Route path="admin/settings" element={<AdminConfigPage />} />
          <Route path="admin/emails" element={<EmailsAdminPage />} />
          <Route path="admin/reminders" element={<RemindersAdminPage />} />
          <Route path="configuracion" element={<SettingsPage />} />
          <Route path="calendario" element={<CalendarPage />} />
        </Route>
        <Route path="importar" element={<ImportPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter>
          <AuthProvider>
            <SnackbarProvider>
              <AppRoutes />
            </SnackbarProvider>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
